import React, { useEffect, useLayoutEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Menu, X, Home, Trophy, Crown, Gem, Target,
  Youtube, Twitter, Store, ChevronDown, MessageSquare, Radio, Bell, LogOut, Gamepad2,
} from "lucide-react";
import { Button } from "./ui/button";
import { useTheme } from "../theme";
import { useAuth } from "../auth";
import { useKickStatus } from "../hooks/useKickStatus";
import { KickLogo, DiscordLogo } from './SocialIcons';
import { REWARDS_DISCORD_URL } from '../content/rewards';
import { SOCIAL_URLS } from '../content/socials';
import { NotificationBell } from './notifications/NotificationBell';
import { ConstructionIndicator } from './site/ConstructionIndicator';
import { OriginalsSectionHeader } from "./originals/OriginalsSectionHeader";
import { originalGames } from "./originals/games";
import { ProfileMenu } from './account/ProfileMenu';
import { ChatSidebar } from './chat/ChatSidebar';
import './sidebar.css';
import './sidebarSocials.css';
import { useMobileViewport } from '../hooks/useMobileViewport';
import { MobileBottomNavigation } from './mobile/MobileBottomNavigation';
import { MobileAccountWallet } from './mobile/MobileAccountWallet';
import { useMobileMenuFocus } from './mobile/useMobileMenuFocus';
import { ChatMentionBadge } from './chat/ChatMentionBadge';
import { HeaderDailyCredits } from './account/HeaderDailyCredits';

const SOCIAL_LINKS = {
  youtube: SOCIAL_URLS.youtube,
  kick: "https://kick.com/greenvenom",
  twitter: SOCIAL_URLS.twitter,
};

// Main navigation (real routes / anchors)
const mainItems = [
  { label: "Home", icon: Home, to: "/", type: "route" },
  { label: "Leaderboard", icon: Trophy, to: "/leaderboard", badge: "$25K", type: "route" },
  { label: "Wager Rewards", icon: Gem, to: "/rewards", type: "route" },
  { label: "VIP Rewards", icon: Crown, to: "/vip-rewards", type: "route" },
  { label: "Challenges", icon: Target, type: "soon" },
  { label: "Store", icon: Store, type: "soon" },
];

// Keep navigation focused on playable games; upcoming previews live in the gallery only.
const gameItems = originalGames.filter((game) => game.available).map(({ name, icon, to }) => ({ label: name, icon, to, type: 'route' }));

// Social media (external)
const socialItems = [
  { label: "YouTube", icon: Youtube, href: SOCIAL_LINKS.youtube },
  { label: "Kick", icon: KickLogo, href: SOCIAL_LINKS.kick },
  { label: "Twitter", icon: Twitter, href: SOCIAL_LINKS.twitter },
  { label: "Discord", icon: DiscordLogo, href: REWARDS_DISCORD_URL },
];

const NavList = ({ collapsed, pathname, onNavigate, kickStatus, restricted = false, testIdPrefix = "sidebar" }) => {
  const [open, setOpen] = useState({ games: true, social: true });
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  const isActive = (to) => pathname === to || (to === "/" && pathname === "/home");

  const SectionHeader = ({ id, label, icon: Icon }) => {
    if (collapsed) return null;
    return (
      <button
        type="button"
        onClick={() => toggle(id)}
        data-testid={`${testIdPrefix}-section-${id}`}
        aria-expanded={open[id]} aria-controls={`${testIdPrefix}-${id}-items`}
        className="sidebar-section-heading group/section w-full flex items-center justify-between px-3.5 pt-4 pb-1.5"
      >
        <Icon size={16} aria-hidden="true" />
        <span data-testid={`${testIdPrefix}-section-${id}-label`} className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 group-hover/section:text-slate-300 transition-colors">
          {label}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-slate-500 transition-transform duration-200 ${open[id] ? "" : "-rotate-90"}`}
        />
      </button>
    );
  };

  const rowCls = (active) =>
    `sidebar-nav-item group flex items-center ${collapsed ? "justify-center px-0" : "justify-between px-3.5"} py-2.5 rounded-xl transition-colors ${
      active ? "bg-emerald-500/15 ring-1 ring-emerald-400/25" : "hover:bg-white/5"
    }`;

  const Inner = ({ item, active }) => (
    <>
      <span className={`flex items-center gap-3 ${collapsed ? "justify-center w-full" : ""}`}>
        <item.icon data-testid={`${testIdPrefix}-icon-${item.label.toLowerCase().replace(/\s+/g, '-')}`} className={`h-5 w-5 shrink-0 ${active ? "text-emerald-300" : "text-slate-400 group-hover:text-white"}`} />
        {!collapsed && (
          <span data-testid={`${testIdPrefix}-label-${item.label.toLowerCase().replace(/\s+/g, '-')}`} className={`sidebar-nav-label font-medium tracking-wide ${active ? "text-white" : "text-slate-300 group-hover:text-white"}`}>
            {item.label}
          </span>
        )}
      </span>
      {!collapsed && item.badge && (
        <span data-testid={`${testIdPrefix}-leaderboard-prize-badge`} className="sidebar-prize-badge text-[11px] font-bold px-2 py-0.5 rounded-md bg-green-500/20 text-green-400 ring-1 ring-green-400/40 shadow-[0_0_6px_rgba(74,222,128,0.45)]">
          {item.badge}
        </span>
      )}
    </>
  );

  const renderItem = (item) => {
    const active = item.to ? isActive(item.to) : false;
    if (item.type === "route") {
      if (restricted) return <button key={item.label} type="button" disabled aria-disabled="true" title={`${item.label} — Under construction`} data-testid={`${testIdPrefix}-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`} className={`${rowCls(false)} is-unavailable w-full`}><Inner item={item} active={false} /></button>;
      return (
        <Link key={item.label} to={item.to} onClick={onNavigate} title={item.label}
          data-testid={`${testIdPrefix}-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`} aria-current={active ? 'page' : undefined}
          className={rowCls(active)}>
          <Inner item={item} active={active} />
        </Link>
      );
    }
    if (item.type === "anchor") {
      return (
        <a key={item.label} href={item.to} onClick={onNavigate} title={item.label}
          data-testid={`${testIdPrefix}-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={rowCls(active)}>
          <Inner item={item} active={active} />
        </a>
      );
    }
    if (item.href) {
      const kickLabel = kickStatus?.state === 'live' ? 'Online' : kickStatus?.state === 'offline' ? 'Offline' : 'Status unavailable';
      return (
        <a key={item.label} href={item.label === 'Kick' ? kickStatus?.url || item.href : item.href} target="_blank" rel="noopener noreferrer" onClick={onNavigate} title={item.label === 'Kick' ? `Kick — ${kickLabel}` : item.label}
          data-testid={`${testIdPrefix}-social-${item.label.toLowerCase()}`}
          data-social-brand={item.label.toLowerCase()}
          className={`${rowCls(false)} sidebar-social-link`}>
          <Inner item={item} active={false} />
          {!collapsed && item.label === 'Kick' && <span className={`text-[11px] font-medium whitespace-nowrap ${kickStatus?.state === 'live' ? 'text-green-400' : 'text-slate-500'}`} data-testid={`${testIdPrefix}-kick-status`} aria-label={kickLabel}>{kickLabel === 'Status unavailable' ? 'Unavailable' : kickLabel}</span>}
        </a>
      );
    }
    // Unavailable destinations are deliberately non-interactive.
    return (
      <button key={item.label} type="button" disabled aria-disabled="true"
        aria-label={`${item.label} — Coming soon`} title={collapsed ? `${item.label} — Coming soon` : undefined}
        data-testid={`${testIdPrefix}-soon-${item.label.toLowerCase()}`}
        className={`${rowCls(false)} is-unavailable w-full text-left`}>
        <Inner item={item} active={false} />
        {!collapsed && <span className="sidebar-coming-soon" data-testid={`${testIdPrefix}-coming-soon-${item.label.toLowerCase()}`}>Coming soon</span>}
      </button>
    );
  };

  const renderGame = (item) => restricted ? <button key={item.label} type="button" disabled aria-disabled="true" title={`${item.label} — Under construction`} data-testid={`${testIdPrefix}-game-${item.label.toLowerCase()}`} className={`${rowCls(false)} is-unavailable w-full`}><Inner item={item} active={false} /></button> : item.type === 'route' && item.to ? (
    <Link key={item.label} to={item.to} onClick={onNavigate} title={item.label} aria-current={isActive(item.to) ? "page" : undefined}
      data-testid={`${testIdPrefix}-game-${item.label.toLowerCase()}`} className={rowCls(isActive(item.to))}>
      <Inner item={item} active={isActive(item.to)} />
    </Link>
  ) : (
    <button type="button" disabled aria-disabled="true"
      key={item.label}
      title={`${item.label} — coming soon`}
      data-testid={`${testIdPrefix}-game-${item.label.toLowerCase()}`}
      aria-label={`${item.label} — Coming soon`}
      className={`${rowCls(false)} is-unavailable w-full text-left`}
    >
      <span className={`flex items-center gap-3 ${collapsed ? "justify-center w-full" : ""}`}>
        <item.icon data-testid={`${testIdPrefix}-game-icon-${item.label.toLowerCase()}`} className="h-5 w-5 shrink-0 text-slate-500" />
        {!collapsed && <span data-testid={`${testIdPrefix}-game-label-${item.label.toLowerCase()}`} className="sidebar-nav-label font-medium text-slate-400">{item.label}</span>}
      </span>
      {!collapsed && (
        <span data-testid={`${testIdPrefix}-game-status-${item.label.toLowerCase()}`} className="sidebar-coming-soon">
          Coming soon
        </span>
      )}
    </button>
  );

  return (
    <nav data-testid={`${testIdPrefix}-navigation`} className="sidebar-navigation font-sidebar flex-1 overflow-y-auto px-3 pb-4 space-y-1">
      <div className="sidebar-main-links" data-testid={`${testIdPrefix}-main-links`}>
      <div className="sidebar-group-items">{mainItems.map(renderItem)}</div>
      </div>
      <div className="sidebar-nav-group" data-testid={`${testIdPrefix}-group-originals`} data-collapsed={!open.games} data-mini={collapsed}>
      {restricted ? <button type="button" disabled aria-disabled="true" title="Originals — Under construction" data-testid={`${testIdPrefix}-link-originals`} className={`${rowCls(false)} is-unavailable w-full`}><Gamepad2 size={20} aria-hidden="true" /></button> : <OriginalsSectionHeader collapsed={collapsed} active={isActive('/originals')} expanded={open.games}
        onToggle={() => toggle('games')} onNavigate={onNavigate} testIdPrefix={testIdPrefix} />}
      <div id={`${testIdPrefix}-originals-games`} hidden={!collapsed && !open.games} className="sidebar-group-items">
        {gameItems.map(renderGame)}
      </div>
      </div>
      <div className="sidebar-nav-group" data-testid={`${testIdPrefix}-group-social`} data-collapsed={!open.social} data-mini={collapsed}>
      <SectionHeader id="social" label="Social Media" icon={Radio} />
      <div id={`${testIdPrefix}-social-items`} hidden={!collapsed && !open.social} className="sidebar-group-items">{socialItems.map(renderItem)}</div>
      </div>
    </nav>
  );
};

const Sidebar = ({ onAuth, balanceSlot, restricted = false }) => {
  const kickStatus = useKickStatus(!restricted);
  const [open, setOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(() => !restricted && sessionStorage.getItem('gv_chat_open') === 'true');
  const toggleChat = (value) => { setChatOpen(value); sessionStorage.setItem('gv_chat_open', String(value)); };
  useLayoutEffect(() => { document.documentElement.dataset.chatOpen = String(chatOpen); return () => { delete document.documentElement.dataset.chatOpen; }; }, [chatOpen]);
  const { theme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const logo = theme !== "venom" ? "/gv-logo-onyx.png" : "/gv-logo.png";
  const [savedCollapsed, setCollapsed] = useState(
    () => localStorage.getItem("gv_sidebar") === "mini"
  );
  const collapsed = restricted || savedCollapsed;
  const { pathname } = useLocation();
  const mobile = useMobileViewport();
  const menuPanel = useMobileMenuFocus(open && mobile && !restricted, () => setOpen(false));
  useEffect(() => { setOpen(false); }, [pathname, mobile]);

  useEffect(() => {
    document.documentElement.setAttribute("data-sidebar", collapsed ? "mini" : "full");
    if (!restricted) localStorage.setItem("gv_sidebar", collapsed ? "mini" : "full");
  }, [collapsed, restricted]);

  return (
    <>
      {/* Fixed top header */}
      <header data-testid="site-header" className={`site-header ${restricted ? 'maintenance-header' : ''} fixed top-0 inset-x-0 z-50 h-16 flex items-center justify-between px-4 bg-[#0b0d11]/90 backdrop-blur-xl border-b border-emerald-500/10 ${balanceSlot ? "keno-header" : ""}`}>
        <div className="header-brand-group flex items-center gap-1 sm:gap-3">
          <button
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="sidebar-open-menu"
            disabled={restricted}
            className="lg:hidden text-white p-2 -ml-2 rounded-lg hover:bg-white/5"
          >
            <Menu className="h-6 w-6" />
          </button>
          <button
            onClick={() => setCollapsed((c) => !c)}
            aria-label="Toggle sidebar"
            data-testid="sidebar-toggle"
            disabled={restricted}
            className="hidden lg:inline-flex text-white p-2 rounded-lg hover:bg-white/5"
          >
            <Menu className="h-6 w-6" />
          </button>
          <Link to="/" data-testid="header-home-link" aria-disabled={restricted || undefined} tabIndex={restricted ? -1 : undefined} onClick={(event) => { if (restricted) event.preventDefault(); }}>
            <img src={logo} alt="GVRewards" data-testid="header-brand-logo" className="header-brand-image h-7 sm:h-8 w-auto max-w-[100px] sm:max-w-none object-contain logo-glow" />
            <img src={theme === 'venom' ? '/v-credit.png' : '/v-credit-onyx.png'} alt="GVRewards" data-testid="header-mobile-brand-logo" className="header-mobile-brand" />
          </Link>
        </div>

        {balanceSlot && <div className="header-game-balance">{balanceSlot}</div>}
        {user && !balanceSlot && !restricted && <MobileAccountWallet key={user.id} />}
        <div className="header-account-actions flex items-center gap-2 sm:gap-3">
          {user ? (
            restricted ? <><button type="button" disabled className="notification-bell" aria-label="Notifications unavailable during construction" data-testid="maintenance-header-bell"><Bell size={20} /></button><Button variant="ghost" onClick={logout} data-testid="maintenance-header-logout" aria-label="Log out"><LogOut size={18} /></Button></> : <><HeaderDailyCredits key={user.id} owner={user.id} /><ConstructionIndicator /><NotificationBell /><ProfileMenu onWallet={React.isValidElement(balanceSlot) ? balanceSlot.props.onWallet : undefined} walletDisabled={React.isValidElement(balanceSlot) && (balanceSlot.props.disabled || balanceSlot.props.loading)} /></>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => onAuth?.("login")}
                data-testid="header-login"
                className="text-slate-200 hover:text-white hover:bg-white/5 h-9 px-2 sm:px-4 text-xs sm:text-sm"
              >
                Log In
              </Button>
              <Button
                onClick={() => onAuth?.("signup")}
                data-testid="header-signup"
                disabled={restricted}
                className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold h-9 px-2 sm:px-4 text-xs sm:text-sm"
              >
                Sign Up
              </Button>
            </>
          )}
          <button type="button" className="header-chat-toggle" data-testid="header-chat-toggle" disabled={restricted} aria-label={chatOpen ? 'Close community chat' : 'Open community chat'} aria-expanded={chatOpen} title="Community chat" onClick={() => toggleChat(!chatOpen)}><MessageSquare size={20} aria-hidden="true" /><ChatMentionBadge prefix="header" /></button>
        </div>
      </header>

      {/* Desktop sidebar (below header) */}
      <aside
        data-testid="desktop-sidebar"
        className={`sidebar-host ${restricted ? 'maintenance-sidebar flex' : 'hidden lg:flex'} fixed left-0 top-16 bottom-0 z-40 flex-col bg-[#0b0d11]/85 backdrop-blur-xl border-r border-emerald-500/10 transition-[width] duration-300 ${
          collapsed ? "w-20" : "w-64"
        }`}
      >
        <NavList collapsed={collapsed} pathname={pathname} kickStatus={kickStatus} restricted={restricted} />
      </aside>

      {/* Mobile drawer */}
      {open && !restricted && (
        <div className="lg:hidden fixed inset-0 z-[60]" data-testid="mobile-sidebar-drawer" id="mobile-navigation-drawer" role="dialog" aria-modal="true" aria-label="Site menu">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="mobile-sidebar-backdrop" />
          <aside ref={menuPanel} data-testid="mobile-sidebar-panel" className="sidebar-drawer-panel absolute inset-y-0 left-0 w-72 max-w-full bg-[#0b0d11] border-r border-emerald-500/10 shadow-2xl flex flex-col">
            <div className="sidebar-drawer-heading h-16 flex items-center justify-between px-5 border-b border-white/5 shrink-0">
              <img src={logo} alt="GVRewards" data-testid="mobile-sidebar-brand-logo" width="122" height="28" className="h-7 w-[122px] object-contain shrink-0 logo-glow" />
              <button onClick={() => setOpen(false)} data-testid="mobile-sidebar-close" aria-label="Close menu" className="text-slate-300 hover:text-white p-2">
                <X className="h-5 w-5" />
              </button>
            </div>
            <NavList collapsed={false} pathname={pathname} onNavigate={() => setOpen(false)} kickStatus={kickStatus} testIdPrefix="mobile-sidebar" />
            <div className="px-3 py-4 border-t border-white/5 space-y-2 shrink-0">
              {user ? (
                <>
                  <Link
                    to="/account"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-sidebar-account"
                    className="flex items-center justify-center gap-2 w-full h-10 rounded-md border border-emerald-500/30 bg-white/5 text-white hover:bg-white/10"
                  >
                    My Account
                  </Link>
                  <Button
                    onClick={() => { logout(); setOpen(false); navigate("/"); }}
                    data-testid="mobile-sidebar-logout"
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
                  >
                    Log Out
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => { onAuth?.("login"); setOpen(false); }}
                    data-testid="mobile-sidebar-login"
                    className="w-full border-emerald-500/30 bg-white/5 text-white hover:bg-white/10"
                  >
                    Log In
                  </Button>
                  <Button
                    onClick={() => { onAuth?.("signup"); setOpen(false); }}
                    data-testid="mobile-sidebar-signup"
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
                  >
                    Sign Up
                  </Button>
                </>
              )}
            </div>
          </aside>
        </div>
      )}

      {!restricted && <ChatSidebar open={chatOpen} onClose={() => { toggleChat(false); requestAnimationFrame(() => document.querySelector(mobile ? '[data-testid="mobile-nav-chat"]' : '[data-testid="header-chat-toggle"]')?.focus()); }} onAuth={(mode) => { toggleChat(false); if (onAuth) onAuth(mode); else navigate('/'); }} />}
      {mobile && !restricted && <MobileBottomNavigation pathname={pathname} menuOpen={open} chatOpen={chatOpen} onMenu={() => { toggleChat(false); setOpen(!open); }} onChat={() => { setOpen(false); toggleChat(!chatOpen); }} onNavigate={() => { setOpen(false); toggleChat(false); }} />}
    </>
  );
};

export default Sidebar;
